// Small column-major 4×4 helpers for the mock viewport and for projecting points with the
// `camera` event's viewProj (world mm → clip).

import type { Orbit, Preset, Vec3 } from './api.ts';

export type Mat4 = Float32Array;

export function lookAt(eye: Vec3, target: Vec3, up: Vec3): Mat4 {
  const zx = eye[0] - target[0], zy = eye[1] - target[1], zz = eye[2] - target[2];
  let l = Math.hypot(zx, zy, zz) || 1;
  const z: Vec3 = [zx / l, zy / l, zz / l];
  let xx = up[1] * z[2] - up[2] * z[1], xy = up[2] * z[0] - up[0] * z[2], xz = up[0] * z[1] - up[1] * z[0];
  l = Math.hypot(xx, xy, xz) || 1;
  xx /= l; xy /= l; xz /= l;
  const y: Vec3 = [z[1] * xz - z[2] * xy, z[2] * xx - z[0] * xz, z[0] * xy - z[1] * xx];
  const m = new Float32Array(16);
  m[0] = xx; m[4] = xy; m[8] = xz;
  m[1] = y[0]; m[5] = y[1]; m[9] = y[2];
  m[2] = z[0]; m[6] = z[1]; m[10] = z[2];
  m[12] = -(xx * eye[0] + xy * eye[1] + xz * eye[2]);
  m[13] = -(y[0] * eye[0] + y[1] * eye[1] + y[2] * eye[2]);
  m[14] = -(z[0] * eye[0] + z[1] * eye[1] + z[2] * eye[2]);
  m[15] = 1;
  return m;
}

/** WebGPU-style perspective: clip z in 0..1. */
export function perspective(fovYDeg: number, aspect: number, near: number, far: number): Mat4 {
  const f = 1 / Math.tan((fovYDeg * Math.PI) / 360);
  const m = new Float32Array(16);
  m[0] = f / aspect;
  m[5] = f;
  m[10] = far / (near - far);
  m[11] = -1;
  m[14] = (near * far) / (near - far);
  return m;
}

export function multiply(a: Mat4, b: Mat4): Mat4 {
  const m = new Float32Array(16);
  for (let c = 0; c < 4; c++)
    for (let r = 0; r < 4; r++) {
      let s = 0;
      for (let k = 0; k < 4; k++) s += a[k * 4 + r]! * b[c * 4 + k]!;
      m[c * 4 + r] = s;
    }
  return m;
}

/** World point → CSS px and depth (0 near … 1 far); `behind` when it's behind the camera. */
export function project(viewProj: Mat4, p: Vec3, width: number, height: number): { x: number; y: number; depth: number; behind: boolean } {
  const x = viewProj[0]! * p[0] + viewProj[4]! * p[1] + viewProj[8]! * p[2] + viewProj[12]!;
  const y = viewProj[1]! * p[0] + viewProj[5]! * p[1] + viewProj[9]! * p[2] + viewProj[13]!;
  const z = viewProj[2]! * p[0] + viewProj[6]! * p[1] + viewProj[10]! * p[2] + viewProj[14]!;
  const w = viewProj[3]! * p[0] + viewProj[7]! * p[1] + viewProj[11]! * p[2] + viewProj[15]!;
  const behind = w <= 1e-6;
  const iw = 1 / (Math.abs(w) < 1e-6 ? 1e-6 : w);
  return { x: (x * iw * 0.5 + 0.5) * width, y: (1 - (y * iw * 0.5 + 0.5)) * height, depth: z * iw, behind };
}

/** RAS+: yaw 0 looks from anterior (+y) towards the target; yaw 90 from the right; pitch up is from above. */
export const PRESETS: Record<Preset, { yawDeg: number; pitchDeg: number }> = {
  hero: { yawDeg: -38, pitchDeg: 18 },
  anterior: { yawDeg: 0, pitchDeg: 0 },
  posterior: { yawDeg: 180, pitchDeg: 0 },
  left: { yawDeg: -90, pitchDeg: 0 },
  right: { yawDeg: 90, pitchDeg: 0 },
  // From above with the eye just behind, so anterior is at the top of the screen.
  superior: { yawDeg: 180, pitchDeg: 89 },
  inferior: { yawDeg: 0, pitchDeg: -89 },
};

export function eyeOf(o: Orbit): Vec3 {
  const y = (o.yawDeg * Math.PI) / 180, p = (o.pitchDeg * Math.PI) / 180;
  return [o.targetMm[0] + o.distanceMm * Math.cos(p) * Math.sin(y), o.targetMm[1] + o.distanceMm * Math.cos(p) * Math.cos(y), o.targetMm[2] + o.distanceMm * Math.sin(p)];
}

/** Up is superior; pitch stays within ±89°, so it never lines up with the view direction. */
export const UP: Vec3 = [0, 0, 1];
